/**
 * Calm Command — biome-specific ambient weather particle system.
 *
 * Extracted verbatim from CalmCommand.jsx. Two exports:
 *   buildWeather(theme, W, H) — returns { theme, parts[] } deterministic per theme
 *   drawWeather(ctx, weather, biome, W, H, horY, t) — paints the drifting layer
 */
import { TAU, seededRng } from './constants';

export function buildWeather(theme, W, H) {
  const rng = seededRng(theme.length * 131 + 7);
  const count = 55;
  const parts = Array.from({ length: count }, () => ({
    x: rng() * W,
    y: rng() * H,
    r: 0.6 + rng() * 1.8,
    phase: rng() * TAU,
    seed: rng(),
  }));
  return { theme, parts };
}

export function drawWeather(ctx, weather, biome, W, H, horY, t) {
  if (!weather || !weather.parts) return;
  const th = weather.theme;

  if (th === 'city') {
    for (const p of weather.parts) {
      const y = (p.y - t * (18 + p.seed * 20)) % H;
      const yy = y < 0 ? y + H : y;
      const wave = Math.sin(t * 1.4 + p.phase) * 6;
      const color = p.seed < 0.5 ? '#7dfaff' : '#ff5cff';
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = 6;
      ctx.globalAlpha = 0.45 + Math.sin(t * 2 + p.phase) * 0.25;
      ctx.fillStyle = color;
      ctx.fillRect(p.x + wave, yy, p.r, p.r);
      ctx.restore();
    }
    return;
  }
  if (th === 'forest') {
    for (const p of weather.parts) {
      const y = (p.y + t * (10 + p.seed * 16)) % H;
      const drift = Math.sin(t * 0.9 + p.phase) * 20;
      ctx.save();
      ctx.shadowColor = biome.accent;
      ctx.shadowBlur = 5;
      ctx.globalAlpha = 0.4 + Math.sin(t * 1.6 + p.phase) * 0.2;
      ctx.fillStyle = biome.accent;
      ctx.beginPath();
      ctx.arc(p.x + drift, y, p.r, 0, TAU);
      ctx.fill();
      ctx.restore();
    }
    return;
  }
  if (th === 'desert') {
    ctx.strokeStyle = `${biome.accent}55`;
    ctx.lineWidth = 1;
    for (const p of weather.parts) {
      const x = (p.x + t * (60 + p.seed * 80)) % (W + 40);
      const y = p.y * 0.9;
      const len = 6 + p.seed * 12;
      ctx.globalAlpha = 0.25 + p.seed * 0.4;
      ctx.beginPath();
      ctx.moveTo(x - len, y);
      ctx.lineTo(x, y);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    return;
  }
  if (th === 'coast') {
    for (const p of weather.parts) {
      const y = (p.y + t * (55 + p.seed * 40)) % H;
      const drift = Math.sin(t * 2 + p.phase) * 4;
      ctx.save();
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = biome.accent;
      ctx.fillRect(p.x + drift, y, 1, 3);
      ctx.restore();
    }
    return;
  }
  if (th === 'mountains') {
    for (const p of weather.parts) {
      const y = (p.y + t * (24 + p.seed * 18)) % H;
      const drift = Math.sin(t * 0.7 + p.phase) * 14;
      ctx.save();
      ctx.shadowColor = '#ffffff';
      ctx.shadowBlur = 3;
      ctx.globalAlpha = 0.75;
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(p.x + drift, y, p.r, 0, TAU);
      ctx.fill();
      ctx.restore();
    }
    return;
  }
  if (th === 'prism') {
    const palette = ['#7dfaff', '#ff5cff', '#39ffcf', '#ffcf3a', '#ff3a7d', '#c17dff'];
    for (const p of weather.parts) {
      const y = (p.y - t * (8 + p.seed * 14)) % H;
      const yy = y < 0 ? y + H : y;
      const color = palette[Math.floor(p.seed * palette.length)];
      const pulse = 0.7 + 0.6 * Math.sin(t * 3 + p.phase);
      ctx.save();
      ctx.shadowColor = color;
      ctx.shadowBlur = 8;
      ctx.globalAlpha = 0.55 * pulse;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(p.x, yy, p.r * pulse, 0, TAU);
      ctx.fill();
      ctx.restore();
    }
  }
}
